// One Copilot conversation in the side panel: load it, send a message with
// the page it came from and its @ references, follow the turn as it streams,
// and react to what the turn produced (plans, an Open action, a page change).
// The box stays open while Copilot works: a message sent meanwhile is queued
// and Copilot reads it after its next step; one it never got to goes out as
// the next turn, or back to the box when the person stops.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  createOperatorChatApi, getOperatorChatApi, abortOperatorTurnApi, streamOperatorMessage,
  queueOperatorMessageApi, discardQueuedApi,
} from "../../store/actions/operatorAction";

const UNREAD = ["waiting", "left"];

export default function useCopilotChat(chatId, { onChatCreated, onNavigate, getContext, onRestoreDraft } = {}) {
  const [messages, setMessages] = useState([]);
  const [plans, setPlans] = useState({});
  // Task cards (proposals, agent decisions), shown under the answer that made them.
  const [tasks, setTasks] = useState({});
  // What the team is doing in this turn (plain-language steps), for the status line.
  const [activity, setActivity] = useState([]);
  const [startedAt, setStartedAt] = useState(null);
  // Task cards that arrived during the turn still running.
  const [liveTaskIds, setLiveTaskIds] = useState([]);
  const [title, setTitle] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [interim, setInterim] = useState([]);
  const [liveAnswer, setLiveAnswer] = useState("");
  const [liveActions, setLiveActions] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const streamingRef = useRef(false);
  const controllerRef = useRef(null);
  const stoppedRef = useRef(false);
  // Sent while the turn was ending, too late to queue: goes out next.
  const pendingRef = useRef([]);
  const sendRef = useRef(null);
  const optsRef = useRef({ getContext, onRestoreDraft });
  optsRef.current = { getContext, onRestoreDraft };

  // quiet: the reload after a turn. The live answer stays until the saved one
  // arrives, then both swap in one render (no blank, no double).
  const load = useCallback(async (id, { quiet = false, swapped } = {}) => {
    if (!id) { setMessages([]); setPlans({}); setTasks({}); setTitle(""); if (swapped) swapped(); return []; }
    if (!quiet) setLoading(true);
    try {
      const data = await getOperatorChatApi(id);
      setTitle(data.chat?.title || "");
      setMessages(data.messages || []);
      setPlans(Object.fromEntries((data.plans || []).map((p) => [p._id, p])));
      setTasks(Object.fromEntries((data.tasks || []).map((t) => [t._id, t])));
      return data.messages || [];
    } catch (_) {
      // Deleted or not reachable: start fresh rather than show an error.
      setMessages([]); setPlans({}); setTitle("");
      onChatCreated?.(null);
      return [];
    } finally {
      if (swapped) swapped();
      if (!quiet) setLoading(false);
    }
  }, [onChatCreated]);

  useEffect(() => { if (!streamingRef.current) load(chatId); }, [chatId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = useCallback(async (content, { context, refs, queuedIds, retryId, attachments, echo = true, continuePlan } = {}) => {
    const text = String(content || "").trim();
    const files = attachments || [];
    if ((!text && !files.length && !(queuedIds && queuedIds.length) && !retryId && !continuePlan) || streamingRef.current) return;
    streamingRef.current = true;
    stoppedRef.current = false;
    setError(null);
    let id = chatId;
    try {
      if (!id) {
        const chat = await createOperatorChatApi();
        id = chat._id;
        onChatCreated?.(id);
      }
    } catch (_) {
      streamingRef.current = false;
      setError("A new conversation could not be started.");
      return;
    }
    setStreaming(true);
    setInterim([]);
    setLiveAnswer("");
    setLiveActions([]);
    setActivity([]);
    setLiveTaskIds([]);
    setStartedAt(Date.now());
    if ((text || files.length) && echo) {
      setMessages((m) => [...m, { _id: `local-${Date.now()}`, role: "user", content: text, context: context?.page || null, refs, attachments: files.length ? files : undefined, createdAt: new Date().toISOString() }]);
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    let failed = null;
    try {
      await streamOperatorMessage(id, text, {
        signal: controller.signal,
        context: context ? { path: context.path, screen: context.screen || undefined } : null,
        refs: (refs || []).map((r) => ({ type: r.type, id: r.id })),
        queuedIds,
        retryId,
        continuePlan,
        attachments: files.map((f) => f.file_id),
        onEvent: (ev) => {
          if (ev.type === "interim") setInterim((l) => [...l, ev.content]);
          else if (ev.type === "content") setLiveAnswer(ev.content);
          else if (ev.type === "plan") setPlans((p) => ({ ...p, [ev.plan._id]: ev.plan }));
          else if (ev.type === "task" && ev.task) {
            setTasks((t) => ({ ...t, [ev.task._id]: ev.task }));
            setLiveTaskIds((l) => (l.includes(ev.task._id) ? l : [...l, ev.task._id]));
          }
          else if (ev.type === "feed" && ev.entry) {
            // In place: a step renamed once its answer is in keeps its position.
            setActivity((l) => {
              const i = l.findIndex((e) => e.entry_id === ev.entry.entry_id);
              if (i === -1) return [...l, ev.entry].slice(-40);
              const next = l.slice();
              next[i] = { ...next[i], ...ev.entry };
              return next;
            });
          }
          else if (ev.type === "navigate") {
            setLiveActions((l) => [...l, ev]);
            if (ev.auto) onNavigate?.(ev);
          } else if (ev.type === "choose") setLiveActions((l) => [...l, ev]);
          else if (ev.type === "queued") {
            setMessages((m) => m.map((x) => ((ev.ids || []).includes(x._id) ? { ...x, queued: ev.state } : x)));
          } else if (ev.type === "stopped") stoppedRef.current = true;
          else if (ev.type === "error") failed = ev.error;
        },
      });
    } catch (e) {
      if (e.name !== "AbortError") failed = e.message || "The request could not be completed.";
    } finally {
      controllerRef.current = null;
      streamingRef.current = false;
      // Another tab already carried this card on: nothing to say.
      if (failed && !(continuePlan && /Nothing to carry on|already working/i.test(failed))) setError(failed);
      const endLive = () => {
        setStreaming(false);
        setInterim([]);
        setLiveAnswer("");
        setLiveActions([]);
        setActivity([]);
        setLiveTaskIds([]);
        setStartedAt(null);
      };
      const loaded = await load(id, { quiet: true, swapped: endLive });
      // Messages Copilot never got to in that turn.
      const unread = loaded.filter((m) => m.role === "user" && UNREAD.includes(m.queued));
      const late = pendingRef.current.splice(0);
      if (stoppedRef.current || failed) {
        // Stopped: they go back to the box, as the person left them.
        const texts = [...unread.map((m) => m.content), ...late];
        if (unread.length) {
          await discardQueuedApi(id, unread.map((m) => m._id)).catch(() => null);
          await load(id, { quiet: true });
        }
        if (texts.length) optsRef.current.onRestoreDraft?.(texts.join("\n\n"));
      } else if (unread.length || late.length) {
        const ctx = optsRef.current.getContext?.() || null;
        if (unread.length && late.length) pendingRef.current.push(...late);
        setTimeout(() => {
          if (unread.length) sendRef.current?.("", { context: ctx, queuedIds: unread.map((m) => m._id) });
          else sendRef.current?.(late.join("\n\n"), { context: ctx });
        }, 0);
      }
    }
  }, [chatId, load, onChatCreated, onNavigate]);
  sendRef.current = send;

  /** A message while Copilot works: queued for its next step. */
  const queue = useCallback(async (content, { context } = {}) => {
    const text = String(content || "").trim();
    if (!text) return;
    if (!streamingRef.current || !chatId) { send(text, { context }); return; }
    const local = { _id: `queued-${Date.now()}`, role: "user", content: text, context: context?.page || null, queued: "waiting", createdAt: new Date().toISOString() };
    setMessages((m) => [...m, local]);
    try {
      const out = await queueOperatorMessageApi(chatId, text, context ? { path: context.path } : null);
      if (out && out.queued && out.message) {
        setMessages((m) => m.map((x) => (x._id === local._id ? out.message : x)));
        return;
      }
    } catch (_) { /* falls through: sent as the next turn */ }
    pendingRef.current.push(text);
  }, [chatId, send]);

  const stop = useCallback(async () => {
    stoppedRef.current = true;
    if (chatId) { try { await abortOperatorTurnApi(chatId); } catch (_) { /* best effort */ } }
  }, [chatId]);

  // A plan decided on its card: the card is the record (no extra message).
  const onPlanUpdated = useCallback((out) => {
    if (!out?.plan) return;
    setPlans((p) => ({ ...p, [out.plan._id]: out.plan }));
  }, []);
  // A card that ran carries on by itself: one follow-up turn checks what ran
  // and puts up the fix or the next card (once per plan, never mid-turn).
  const carriedRef = useRef(new Set());
  useEffect(() => {
    if (streaming) return;
    for (const p of Object.values(plans)) {
      if (!p || !p.continue_pending || !["applied", "failed"].includes(p.status) || carriedRef.current.has(p._id)) continue;
      carriedRef.current.add(p._id);
      setTimeout(() => sendRef.current?.("", { continuePlan: p._id, echo: false }), 0);
      break;
    }
  }, [plans, streaming]);
  const onTaskUpdated = useCallback((task) => {
    if (task && task._id) setTasks((t) => ({ ...t, [task._id]: { ...(t[task._id] || {}), ...task } }));
  }, []);
  /** Send a message that got no answer again (it is not posted twice). */
  const retry = useCallback((messageId, { context } = {}) => send("", { retryId: messageId, context }), [send]);

  return {
    messages, plans, tasks, activity, startedAt, liveTaskIds, title, streaming, interim, liveAnswer, liveActions, error, loading,
    send, queue, stop, retry, onPlanUpdated, onTaskUpdated, setError,
  };
}
